import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSupabase } from '../../utils/supabase';
import { Download, FileText, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';

// List of common free email domains to block
const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 
  'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'gmx.com', 'live.com', 'msn.com', 'me.com', 'inbox.com', 'mail.ru',
  'googlemail.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.co.jp', 'yahoo.com.br',
  'hotmail.co.uk', 'hotmail.fr', 'outlook.fr', 'web.de', 'comcast.net',
  'verizon.net', 'att.net', 'sbcglobal.net', 'cox.net', 'charter.net'
];

interface DownloadFormData {
  email: string;
  company: string;
  title: string;
  selectedFormat: string; // Changed from selectedFormats array to single string
  password?: string;
}

interface ResourceDownloadProps {
  resources: Array<{
    id: string;
    title: string;
    description: string;
    type: string;
    formats: string[];
  }>;
  onClose?: () => void;
}

const ResourceDownload: React.FC<ResourceDownloadProps> = ({ resources, onClose }) => {
  const { supabase } = useSupabase();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<DownloadFormData | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<DownloadFormData>();

  useEffect(() => {
    // Check for stored user info in localStorage
    const storedInfo = localStorage.getItem('userDownloadInfo');
    if (storedInfo) {
      setUserInfo(JSON.parse(storedInfo));
    }
  }, []);

  const validateBusinessEmail = (email: string) => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    return !FREE_EMAIL_DOMAINS.includes(domain);
  };

  const onSubmit = async (data: DownloadFormData) => {
    try {
      setError(null);

      if (!validateBusinessEmail(data.email)) {
        setError('Please use a business email address. Free email providers (Gmail, Yahoo, etc.) are not accepted.');
        return;
      }

      // Store user info for future downloads
      localStorage.setItem('userDownloadInfo', JSON.stringify(data));

      // Process download
      const { error: downloadError } = await supabase
        .from('downloads')
        .insert({
          resource_id: resources[0].id,
          user_email: data.email,
          format: data.selectedFormat
        });

      if (downloadError) throw downloadError;

      setSubmitted(true);
    } catch (error) {
      console.error('Download error:', error);
      setError(error instanceof Error ? error.message : 'Failed to process download');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // If we have stored user info, show simplified form
  const renderForm = () => {
    if (userInfo) {
      return (
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-blue-800">Downloading as: {userInfo.email}</p>
            <p className="text-blue-600 text-sm">{userInfo.company} - {userInfo.title}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Format
            </label>
            <div className="space-y-2">
              {resources[0].formats.map(format => (
                <label key={format} className="flex items-center">
                  <input
                    type="radio"
                    {...register('selectedFormat')}
                    value={format}
                    className="form-radio h-4 w-4 text-blue-600"
                  />
                  <span className="ml-2">{format.toUpperCase()}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => setUserInfo(null)}
              className="text-blue-600 hover:text-blue-800"
            >
              Use Different Email
            </button>
            <button
              type="submit"
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Download className="h-5 w-5 mr-2" />
              Download
            </button>
          </div>
        </div>
      );
    }

    // Full form for new users
    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Business Email * <span className="text-xs text-gray-500">(free email providers not accepted)</span>
          </label>
          <input
            type="email"
            {...register('email', { required: 'Email is required' })}
            className="w-full px-4 py-2 border border-gray-300 rounded-md"
            placeholder="your.name@company.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            We only accept business email addresses to ensure proper delivery of important information.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Name *
          </label>
          <input
            type="text"
            {...register('company', { required: 'Company name is required' })}
            className="w-full px-4 py-2 border border-gray-300 rounded-md"
            placeholder="Your Company/Practice"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Job Title *
          </label>
          <input
            type="text"
            {...register('title', { required: 'Job title is required' })}
            className="w-full px-4 py-2 border border-gray-300 rounded-md"
            placeholder="Your Position"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password (if required)
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              {...register('password')}
              className="w-full px-4 py-2 border border-gray-300 rounded-md pr-10"
              placeholder="Enter password if required"
            />
            <button 
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Some resources may require a password to access. Leave blank if not required.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Format
          </label>
          <div className="space-y-2">
            {resources[0].formats.map(format => (
              <label key={format} className="flex items-center">
                <input
                  type="radio"
                  {...register('selectedFormat')}
                  value={format}
                  className="form-radio h-4 w-4 text-blue-600"
                />
                <span className="ml-2">{format.toUpperCase()}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Download Resources</h2>
          <p className="text-gray-600">Select your preferred format to download.</p>
        </div>

        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
            <p className="text-gray-600 mb-6">
              Your download will begin automatically. Check your email for additional resources.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center text-red-700">
                <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {renderForm()}

            {!userInfo && (
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ResourceDownload;